import React from "react";
import Table from "rc-table";
import { calcBayesianStatsRecursive, insertValues } from "./Maths";
import { getSignificantFigures } from "./UserSettings";
import { convertParentData, getParentLabels } from "./Flow";

/*
A group of functions for generating, editing and displaying the conditional probability table in both formats
Some of this is no longer in use (pending cleaning)

*Unliklely to need editing*
*/

//Assigns dataIndex (needed for rc-table) to each column object
function indexDef(cols) {
  const iterate = (obj) => {
    Object.keys(obj).forEach((k) => {
      if (k.toString() === "children" && obj[k].length === 0) {
        obj["dataIndex"] = count;
        count++;
      }
      if (typeof obj[k] === "object" && obj[k] !== null) {
        iterate(obj[k]);
      }
    });
  };
  let count = 0;
  iterate(cols);
  return cols;
}

//Deep recurrsion to generate a list of column names - i.e. "P1C1" from the colData provided
function genColsList(obj, list) {
  Object.keys(obj).forEach((key) => {
    if (key.toString()[0] === "P") {
      list.push(key.toString());
    }

    if (typeof obj[key] === "object" && obj[key] !== null) {
      genColsList(obj[key], list);
    }
  });
}

//Generates a nested array for col structure  e.g. [[P1C1, P1C2], [P2C1, P2C2]]
function cleanCols(cols, parentData) {
  cols = [...new Set(cols)];
  let max = 1;
  let num;
  for (let i = 0; i < cols.length; i++) {
    num = parseInt(
      cols[i]
        .toString()
        .slice(
          cols[i].toString().indexOf("P") + 1,
          cols[i].toString().indexOf("C")
        )
    );
    if (num > max) {
      max = num;
    }
  }

  let newCols = Array.from(Array(max), () => []);
  for (let i = 0; i < cols.length; i++) {
    num = parseInt(
      cols[i]
        .toString()
        .slice(
          cols[i].toString().indexOf("P") + 1,
          cols[i].toString().indexOf("C")
        )
    );
    newCols[num - 1].push(colToObject(cols[i], parentData));
  }

  return newCols;
}

function colToObject(name, parentData) {
  let pNum = name.slice(name.indexOf("P") + 1, name.indexOf("C"));
  let cNum = name.slice(name.indexOf("C") + 1);
  //This check seems to be neccessary to catch some async issues between state updates
  if (
    pNum <= parentData.length &&
    cNum <= Object.values(parentData[pNum - 1])[0].length
  ) {
    return {
      title: Object.keys(Object.values(parentData[pNum - 1])[0][cNum - 1])[0],
      dataIndex: name,
      key: name,
      children: [],
    };
  } else {
    //This never actually renders, but does run during parent updates
    return {
      title: name,
      dataIndex: name,
      key: name,
      children: [],
    };
  }
}

function ConditionalProbTable({ nodeData, id }) {
  const table = nodeData.condProb;
  const parentData = convertParentData(nodeData.parentData);
  const states = nodeData.states;
  const funcs = nodeData.funcs;
  let columns = [];
  let data = [];

  const updateProb = (newVal, r, c) => {
    newVal = newVal.target.value;
    if (isNaN(newVal) || newVal === undefined || newVal === "") {
      newVal = 0;
    } else if (
      !(
        (newVal[newVal.length - 1] === "0" && newVal.includes(".")) ||
        newVal[newVal.length - 1] === "."
      )
    ) {
      newVal = parseFloat(newVal);
    }
    let tableCopy = JSON.parse(JSON.stringify(table));
    let index = itemsPerRow * r + c;
    let values = calcBayesianStatsRecursive(tableCopy);
    values[index] = newVal;
    let newTable = insertValues(tableCopy, values);
    funcs.updateProbVal(JSON.parse(JSON.stringify(newTable)));
  };

  const noParentUpdateProb = (newVal, row) => {
    let tableCopy = JSON.parse(JSON.stringify(table));
    tableCopy[row][Object.keys(tableCopy[row])[0]] = newVal.target.value;
    funcs.updateProbVal(tableCopy);
  };

  //Small table for parentless nodes
  if (parentData.length === 0) {
    columns = [
      { title: "State", dataIndex: "state" },
      { title: "P", dataIndex: "p" },
    ];

    for (let i = 0; i < table.length; i++) {
      if (states[i] !== undefined) {
        data.push({
          state: Object.keys(states[i])[0],
          p: noParentEditableProb(Object.values(table[i])[0], i),
        });
      }
    }
    return <Table columns={columns} data={data} className="condProbTable" />;
  }

  let rawData = calcBayesianStatsRecursive(table);
  let numRows = table.length;
  let itemsPerRow = rawData.length / numRows;

  function editableProb(val, i, j) {
    return (
      <input
        type="text"
        value={val}
        onChange={(e) => updateProb(e, i, j)}
        className="prob-input"
      />
    );
  }

  function noParentEditableProb(val, row) {
    return (
      <input
        type="text"
        value={val}
        onChange={(e) => noParentUpdateProb(e, row)}
        className="prob-input"
      />
    );
  }

  const colData = Object.values(table[0])[0];
  let colsList = [];

  //Currently this is quite inefficient and thrown together quickly - improve if any noticable efficiency issues - especially with large tables!
  genColsList(colData, colsList);
  colsList = cleanCols(colsList, parentData);

  if (colsList.length === 1) {
    columns = colsList[0];
  } else {
    for (let i = colsList.length - 2; i >= 0; i--) {
      for (let j = 0; j < colsList[i].length; j++) {
        colsList[i][j].children = JSON.parse(JSON.stringify(colsList[i + 1]));
        if (i === 0) {
          columns.push(colsList[i][j]);
        }
      }
    }
  }

  columns = indexDef(columns);

  for (let i = 0; i < numRows; i++) {
    let obj = {};
    for (let j = i * itemsPerRow; j < itemsPerRow + i * itemsPerRow; j++) {
      obj[j - i * itemsPerRow] = editableProb(
        rawData[j],
        i,
        j - i * itemsPerRow
      );
    }
    data.push(obj);
  }

  let parentNameCols = [];
  const parentLabels = getParentLabels(id);
  for (let i = 0; i < parentData.length; i++) {
    parentNameCols.push({
      title: parentLabels[i],
      dataIndex: parentLabels[i],
      key: parentLabels[i],
      children: [],
    });
  }
  for (let i = parentNameCols.length - 2; i >= 0; i--) {
    parentNameCols[i].children.push(
      JSON.parse(JSON.stringify(parentNameCols[i + 1]))
    );
  }

  let finalCols = [
    {
      title: "SideCol",
      dataIndex: "SideCol",
      key: "SideCol",
      children: [parentNameCols[0]],
    },
    {
      title: "MainCol",
      dataIndex: "MainCol",
      key: "MainCol",
      children: columns,
    },
  ];

  //Adds the state name to the side column
  for (let i = 0; i < data.length; i++) {
    if (parentNameCols[parentNameCols.length - 1].dataIndex !== undefined) {
      let col = parentNameCols[parentNameCols.length - 1].dataIndex.toString();
      data[i][col] = Object.keys(states[i])[0];
    }
  }

  return <Table columns={finalCols} data={data} className="condProbTable" />;
}

export default ConditionalProbTable;

/*--------------------------------------
 * The following functions all aid the updating of conditional probability tables as nodes and edges change
 * App.js imports the top-level functions from here
 */

//Updates conditional probability table when a new parent is added
export function condProbNewParent(node) {
  let parentData = convertParentData(node.data.parentData);
  if (parentData !== undefined && parentData.length > 0) {
    const numNewStates = Object.values(parentData[parentData.length - 1])[0]
      .length;
    for (let i = 0; i < node.data.condProb.length; i++) {
      node.data.condProb[i] = newParentUpdate(
        node.data.condProb[i],
        parentData.length,
        numNewStates
      );
    }
  }
  return node.data.condProb;
}
//Recursion for condProbNewParent
const newParentUpdate = (obj, parentNum, numNewStates) => {
  let updated = false;
  Object.keys(obj).forEach((key) => {
    if (!isNaN(obj[key])) {
      //if value is a number - i.e. to be replaced with new conditions
      const currVal = obj[key];
      let newVal = []; //To be populated with new parent conditions
      for (let i = 0; i < numNewStates; i++) {
        let newObj = {};
        let newKey = "P" + parentNum + "C" + (i + 1);
        newObj[newKey] = currVal;
        newVal.push(newObj);
      }
      obj[key] = newVal;
      updated = true;
    }

    if (typeof obj[key] === "object" && obj[key] !== null && !updated) {
      newParentUpdate(obj[key], parentNum, numNewStates);
    }
  });
  return obj;
};

//Updates conditional probability table when a new state is added to a parent
export function condProbNewState(node, pIndex) {
  let newValFrac = 1 / node.data.states.length;
  for (let i = 0; i < node.data.condProb.length; i++) {
    if (i === node.data.condProb.length - 1) {
      newValFrac =
        1 -
        newValFrac.toFixed(getSignificantFigures()) *
          (node.data.states.length - 1); //Ensures they add up to 1
    }
    node.data.condProb[i] = newStateUpdate(
      node.data.condProb[i],
      pIndex,
      newValFrac
    );
  }
  return node.data.condProb;
}
//Recursion for condProbNewState
const newStateUpdate = (obj, pIndex, newValFrac, prev = {}) => {
  let updated = false;
  Object.keys(obj).forEach((key) => {
    if (key.toString()[0] === "P") {
      let pNum = parseInt(key.slice(key.indexOf("P") + 1, key.indexOf("C")));
      let cNum = parseInt(key.slice(key.indexOf("C") + 1));
      if (pNum === pIndex + 1 && cNum === 1) {
        let newKey = "P" + pNum + "C" + (Object.keys(prev).length + 1);
        let o = {};

        let newVal = newValFrac;

        if (isNaN(obj[key])) {
          newVal = JSON.parse(JSON.stringify(obj[key]));
          let arr = new Array(calcBayesianStatsRecursive(newVal).length).fill(
            newValFrac
          );
          insertValues(newVal, arr);
        }

        o[newKey] = newVal;
        prev.push(o);
        updated = true;
      }
    }

    if (typeof obj[key] === "object" && obj[key] !== null && !updated) {
      newStateUpdate(obj[key], pIndex, newValFrac, obj);
    }
  });
  return obj;
};

//Updates conditional probability table when a parent is deleted
export function condProbDelParent(node, prevParentData) {
  let values = getValues(node, prevParentData);
  let parentData = node.data.parentData;
  let convertedParentData = convertParentData(node.data.parentData);
  let newCondProb = [];
  for (let i = 0; i < node.data.states.length; i++) {
    let newState = {};
    let key = "S" + (i + 1);
    newState[key] = 1; //Temporay value
    newCondProb.push(newState);
  }
  let newNode = {
    ...node,
    data: { ...node.data, parentData: {}, condProb: newCondProb },
  };
  for (let i = 0; i < convertedParentData.length; i++) {
    newNode.data.parentData[i + 1] = parentData[i + 1];
    newNode = {
      ...newNode,
      data: {
        ...newNode.data,
        condProb: condProbNewParent(JSON.parse(JSON.stringify(newNode))),
      },
    };
  }
  return insertValues(newNode.data.condProb, values);
}

//Gets the values to retain when deleting a parent
function getValues(node, prevParents) {
  let parentData = convertParentData(node.data.parentData);
  let parentIDs = parentData.map((p) => Object.keys(p)[0]);
  let parentLengths = prevParents.map((p) => Object.values(p)[0].length);
  let prevIDs = prevParents.map((p) => Object.keys(p)[0]);
  let delIndex = prevIDs.indexOf(
    prevIDs.filter((p) => !parentIDs.includes(p))[0]
  );
  let origValues = calcBayesianStatsRecursive(node.data.condProb);

  let numVals = parentLengths.slice(delIndex + 1).reduce((a, b) => a * b, 1);
  let gap = numVals * parentLengths[delIndex];

  let newValues = [];
  for (let i = 0; i < origValues.length; i += gap) {
    for (let j = 0; j < numVals; j++) {
      newValues.push(origValues[i + j]);
    }
  }
  return newValues;
}

//Updates conditional probability table when a state is deleted from a parent
export function condProbDelState(node, pNum, cNum) {
  let JSONCondProb = JSON.parse(JSON.stringify(node.data.condProb));
  for (let i = 0; i < JSONCondProb.length; i++) {
    JSONCondProb[i] = condProbEmptyNull(
      delParentStateUpdate(JSONCondProb[i], pNum, cNum)
    );
  }
  return JSONCondProb;
}
//Recursion for condProbDelState
function delParentStateUpdate(obj, pNum, cNum) {
  Object.keys(obj).forEach((key) => {
    if (key.toString()[0] === "P") {
      let objPNum = parseInt(key.slice(key.indexOf("P") + 1, key.indexOf("C")));
      let objCNum = parseInt(key.slice(key.indexOf("C") + 1));
      if (objPNum === pNum && objCNum === cNum) {
        delete obj[key];
      } else if (objPNum === pNum && objCNum > cNum) {
        let oldKey = "P" + objPNum + "C" + objCNum;
        let newKey = "P" + objPNum + "C" + (objCNum - 1);
        delete Object.assign(obj, { [newKey]: obj[oldKey] })[oldKey];
      }
    }
    if (typeof obj[key] === "object" && obj[key] !== null) {
      delParentStateUpdate(obj[key], pNum, cNum);
    }
  });
  return obj;
}
//Helper function for cleaning {} out of conditional probability tables
function condProbEmptyNull(obj) {
  Object.keys(obj).forEach((key) => {
    if (obj[key].constructor === Array) {
      obj[key] = obj[key].filter((value) => Object.keys(value).length !== 0);
    }
    if (typeof obj[key] === "object" && obj[key] !== null) {
      condProbEmptyNull(obj[key]);
    }
  });
  return obj;
}
